const WIDE_TABLE_COLS_MOBILE = 3;
const WIDE_TABLE_COLS_DESKTOP = 5;

const BLOCKED_STYLE_PROPS = new Set([
  'margin',
  'margin-left',
  'margin-right',
  'margin-top',
  'margin-bottom',
  'padding',
  'padding-left',
  'padding-right',
  'padding-top',
  'padding-bottom',
]);

const stripLayoutInlineStyles = (html: string): string =>
  html.replace(/\s+style="([^"]*)"/gi, (_match, css: string) => {
    const kept = css
      .split(';')
      .map((rule) => rule.trim())
      .filter((rule) => {
        if (!rule) {
          return false;
        }

        const prop = rule.split(':')[0]?.trim().toLowerCase();
        return prop ? !BLOCKED_STYLE_PROPS.has(prop) : false;
      });
    return kept.length > 0 ? ` style="${kept.join('; ')}"` : '';
  });

const ANCHOR_TAG_REGEX = /<a\b([^>]*)>/gi;
const HREF_ATTRIBUTE_REGEX = /\shref=(["'])([^"']*)\1/i;
const TARGET_ATTRIBUTE_REGEX = /\starget=/i;
const URL_SCHEME_REGEX = /^[a-z][a-z\d+.-]*:/i;

const normalizeExternalLinks = (html: string): string =>
  html.replace(ANCHOR_TAG_REGEX, (tag, attributes: string) => {
    const href = HREF_ATTRIBUTE_REGEX.exec(attributes)?.[2];

    if (!href) {
      return tag;
    }

    const isInternal = href.startsWith('#') || href.startsWith('/');

    if (isInternal || URL_SCHEME_REGEX.test(href)) {
      return tag;
    }

    const externalAttributes = attributes.replace(
      HREF_ATTRIBUTE_REGEX,
      ` href="https://${href}"`,
    );
    const newTabAttributes = TARGET_ATTRIBUTE_REGEX.test(attributes)
      ? ''
      : ' target="_blank" rel="noopener noreferrer"';

    return `<a${externalAttributes}${newTabAttributes}>`;
  });

const wrapTablesInScroller = (html: string): string =>
  html.replace(
    /<table([^>]*)>([\s\S]*?)<\/table>/gi,
    (_match, attrs: string, content: string) => {
      const firstRow = /<tr[^>]*>([\s\S]*?)<\/tr>/i.exec(content)?.[1] ?? '';
      const cols = (firstRow.match(/<t[hd]\b/gi) ?? []).length;
      const wideMobile =
        cols >= WIDE_TABLE_COLS_MOBILE ? ' data-wide-mobile' : '';
      const wideDesktop =
        cols >= WIDE_TABLE_COLS_DESKTOP ? ' data-wide-desktop' : '';
      return `<div class="rich-text-table-scroll"${wideMobile}${wideDesktop}><table${attrs}>${content}</table></div>`;
    },
  );

export const sanitizeRichTextHtml = (html: string): string =>
  wrapTablesInScroller(normalizeExternalLinks(stripLayoutInlineStyles(html)));

const FIRST_PARAGRAPH_REGEX = /<p\b[^>]*?>[\s\S]*?<\/p>/i;

export const getFirstParagraph = (html: string): string =>
  FIRST_PARAGRAPH_REGEX.exec(html)?.[0].trim() ?? '';

const VOID_TAGS = new Set(['BR', 'HR', 'IMG', 'INPUT', 'WBR']);

const parseHtmlBody = (html: string): HTMLElement =>
  new DOMParser().parseFromString(html, 'text/html').body;

const isBlankTextNode = (node: ChildNode): boolean =>
  node.nodeType === Node.TEXT_NODE && !node.textContent?.trim();

const getLastContentChild = (element: Element): ChildNode | null => {
  let child = element.lastChild;

  while (child && isBlankTextNode(child)) {
    child = child.previousSibling;
  }

  return child;
};

const canContainAppendedHtml = (node: ChildNode | null): node is Element =>
  node instanceof Element &&
  !VOID_TAGS.has(node.tagName) &&
  node.tagName !== 'A';

const findLastTextContainer = (root: Element): Element => {
  let container = root;
  let lastChild = getLastContentChild(container);

  while (canContainAppendedHtml(lastChild)) {
    container = lastChild;
    lastChild = getLastContentChild(container);
  }

  return container;
};

interface TruncateHtmlParams {
  html: string;
  maxLength: number;
}

export const truncateHtml = ({
  html,
  maxLength,
}: TruncateHtmlParams): string => {
  const body = parseHtmlBody(html);
  const plainText = body.textContent ?? '';
  const lastSpaceIndex = plainText.lastIndexOf(' ', maxLength);
  const cutoffIndex = lastSpaceIndex > 0 ? lastSpaceIndex : maxLength;
  let remainingLength = plainText.slice(0, cutoffIndex).trimEnd().length;
  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text;

    if (textNode.data.length < remainingLength) {
      remainingLength -= textNode.data.length;
      continue;
    }

    textNode.data = `${textNode.data.slice(0, remainingLength).trimEnd()}...`;

    const restRange = document.createRange();
    restRange.setStartAfter(textNode);
    restRange.setEndAfter(body.lastChild ?? textNode);
    restRange.deleteContents();
    break;
  }

  return body.innerHTML;
};

export const appendInlineHtml = (html: string, inlineHtml: string): string => {
  const body = parseHtmlBody(html);

  findLastTextContainer(body).insertAdjacentHTML('beforeend', ` ${inlineHtml}`);

  return body.innerHTML;
};

const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

export const escapeHtml = (text: string): string =>
  text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

export const linkifyText = (text: string): string =>
  escapeHtml(text).replace(URL_REGEX, (url) => {
    const href = url.startsWith('http') ? url : `https://${url}`;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
